import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import Button from 'components/ui/button'
import styles from './index.module.css'

const ZERO_WIDTH_SPACE = '​'
type Props = {
  name: string
  placeholder: string
  value: string
  onChange: (name: any, value: string) => void
}

/**
 * An inline edit component that allows users to edit text directly in place.
 * The component automatically adjusts its width based on the content length and provides an edit button.
 *
 * @param name - The name attribute for the input field
 * @param placeholder - Placeholder text shown when value is empty
 * @param value - The current value of the input field
 * @param onChange - Callback function called when the value changes, receives name and new value
 * @returns The inline edit component with input field and edit button
 */
const InlineEdit = (props: Props): React.ReactElement<'div'> => {
  const [width, setWidth] = useState('auto')
  const inputRef = useRef<HTMLInputElement>(null)
  const valueRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    updateWidth()
  }, [props.value])
  useLayoutEffect(() => {
    if (width === 'auto') {
      updateWidth()
    }
  }, [width])

  const updateWidth = () => {
    if (valueRef.current) {
      setWidth(`${valueRef.current.offsetWidth + 8}px`)
    }
  }

  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleChange = (event: any) => {
    const { name, value } = event.target
    setWidth('auto')
    props.onChange(name, value)
  }

  return (
    <div className={styles.inlineEdit}>
      <div className={styles.inlineEditValue} ref={valueRef}>
        <span>
          <span>{props.value || props.placeholder}</span>
          {ZERO_WIDTH_SPACE}
        </span>
      </div>
      <input
        className={styles.inlineEditInput}
        name={props.name}
        placeholder={props.placeholder}
        ref={inputRef}
        value={props.value || ''}
        style={{
          width,
        }}
        onChange={handleChange}
      />
      <Button compact appearance="icon" icon="edit" onClick={focusInput} />
    </div>
  )
}

export default InlineEdit
